import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/seo/SEOHead';

const SymboliekPage: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const symboliekSchema = [
        {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Kleur en Symboliek in de Kunst van Clemens Briels: Analyse van Vier Werken",
            "description": "Concrete analyse van kleurgebruik, symboliek en materiaalwerking in vier werken van Clemens Briels uit een privécollectie: zeefdrukken, origineel schilderij en 3D wandobject. Met firsthand observaties over wat je ziet wanneer je voor de originelen staat.",
            "author": {
                "@type": "Person",
                "name": "Particuliere Verzamelaar",
                "description": "Jarenlange liefhebber en eigenaar van meerdere originele werken van Clemens Briels."
            },
            "publisher": { "@type": "Organization", "name": "Kunstlounge", "url": "https://kunstlounge.nl/" },
            "url": "https://kunstlounge.nl/symboliek",
            "about": {
                "@type": "Person",
                "name": "Clemens Briels",
                "birthDate": "1946",
                "nationality": "Nederlands",
                "jobTitle": "Beeldend Kunstenaar",
                "description": "Grondlegger van het Antipodisme. Officieel Olympisch Kunstenaar bij de Winterspelen van Salt Lake City 2002."
            },
            "breadcrumb": {
                "@type": "BreadcrumbList",
                "itemListElement": [
                    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://kunstlounge.nl/" },
                    { "@type": "ListItem", "position": 2, "name": "Symboliek", "item": "https://kunstlounge.nl/symboliek" }
                ]
            }
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
                {
                    "@type": "Question",
                    "name": "Wat betekent de kleur in het werk van Clemens Briels?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Kleur heeft bij Clemens Briels geen decoratieve functie maar is het primaire communicatiemiddel. Kleurkeuzes volgen een emotionele logica in plaats van realisme: een compositie kan tegelijk warm en koud aanvoelen, afhankelijk van welk vlak de aandacht trekt. Dat is goed zichtbaar in de zeefdruk Antigua, a bay a day."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Waarom zijn de figuren in het werk van Clemens Briels geen portretten?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "De menselijke figuren bij Clemens Briels zijn nooit portrettistisch bedoeld. Het zijn typen en archetypes die een houding of emotie vertegenwoordigen, geen specifieke persoon. In Algunos Gentes Hacen la Comedia, Otros No plaatst hij bijvoorbeeld figuren naast elkaar die elk hun eigen verhouding tot het thema van de titel uitdrukken."
                    }
                },
                {
                    "@type": "Question",
                    "name": "Wat maakt een 3D wandobject van Clemens Briels anders dan een schilderij?",
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": "Een 3D wandobject zoals Yellow Beard of Vegas is acryl op hars met enkele centimeters diepte, waardoor het functioneert als sculptuur in plaats van een vlak beeld. Het harsoppervlak reflecteert licht anders dan doek: matte en glanzende zones wisselen elkaar af afhankelijk van de kijkhoek."
                    }
                }
            ]
        }
    ];

    return (
        <main id="main-content" className="pt-20">
            <SEOHead
                title="Symboliek in de kunst van Clemens Briels | Kunstlounge"
                description="Kleur, symboliek en materiaalwerking in de werken van Clemens Briels: analyse van zeefdrukken, origineel schilderij en 3D wandobject uit een particuliere privécollectie."
                canonical="/symboliek/"
                imageUrl="/images/collectie/algunos-gentes-hacen-la-comedia/algunos-gentes-hacen-la-comedia-otros-no-1.webp"
                schemaData={symboliekSchema}
                hreflangEn="/en/symbolism.html"
                hreflangNl="/symboliek/"
            />
            {/* Hero Section */}
            <section className="relative py-32 bg-white overflow-hidden">
                <div className="container mx-auto px-6">
                    <span className="block text-accent text-xs tracking-[0.4em] uppercase mb-8">
                        Verdieping in de Collectie
                    </span>
                    <h1 className="text-6xl md:text-8xl font-serif text-primary mb-10 leading-tight">
                        Kleur & Symboliek:<br />
                        <span className="text-gray-300 font-light italic">Vier Werken Nader Bekeken</span>
                    </h1>
                    <p className="text-xl text-gray-500 font-light max-w-2xl leading-relaxed">
                        Wat zie je als je voor een origineel Briels staat? Een analyse van kleur, symboliek en materiaalwerking in de vier werken van deze collectie.
                    </p>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6 max-w-4xl">
                    <div className="prose prose-lg max-w-none font-light text-gray-600 leading-relaxed">

                        {/* === BLOK 1: Kleur als communicatiemiddel === */}
                        <h2 className="text-3xl font-serif text-primary mt-4 mb-8 underline decoration-accent decoration-1 underline-offset-8">Kleur als communicatiemiddel, niet als decoratie</h2>
                        <p className="mb-8">
                            In het werk van Clemens Briels heeft kleur geen decoratieve functie. Het is het primaire communicatiemiddel. Kleurkeuzes volgen geen realisme maar een emotionele logica: een compositie kan tegelijk warm en koud aanvoelen, afhankelijk van welk vlak de aandacht trekt.
                        </p>
                        <p className="mb-8">
                            Dat is goed te zien in de zeefdruk <strong><em>Antigua, a bay a day, after 360 days I’ll settle down for 5</em></strong> (kleurenzeefdruk, editie 132/175, 95 bij 110 cm, 2000). De titel beschrijft een zomerreis door 360 baaien van Antigua; de kleuren sluiten daarbij aan maar overtreffen de werkelijkheid. Tropische tinten die nergens gematigd worden, een compositie die ritme uitstraalt. Briels neemt een bestaande plek als vertrekpunt en vergroot die uit naar iets dat je direct herkent maar nog nergens eerder zo hebt gezien. De editie van 175 stuks is volledig uitverkocht bij reguliere galeries; dit is exemplaar 132.
                        </p>

                        {/* === BLOK 2: Menselijke figuur als symbooldrager === */}
                        <h2 className="text-3xl font-serif text-primary mt-20 mb-8 underline decoration-accent decoration-1 underline-offset-8">De menselijke figuur als symbooldrager</h2>
                        <p className="mb-8">
                            Vrijwel alle werken van Briels bevatten menselijke figuren, maar die zijn nooit portrettistisch bedoeld. Het zijn typen, archetypes, symbolen van een houding of emotie. Ze vertegenwoordigen een rol, niet een persoon.
                        </p>
                        <p className="mb-8">
                            Dat is het duidelijkst in <strong><em>Algunos Gentes Hacen la Comedia, Otros No</em></strong> (kleurenzeefdruk, editie 97/175, 104 bij 155 cm, 2005). De titel betekent letterlijk: sommige mensen spelen komedie, anderen niet. Op het panoramisch formaat van 104 bij 155 centimeter plaatst Briels een reeks figuren naast elkaar die elk hun eigen verhouding tot dat thema uitdrukken: de acteur, de toeschouwer, de onverschillige, de betrokken waarnemer. Niet moralistisch: Briels heeft een mildheid in zijn observaties die je terugziet in de kleuren. Zelfs figuren die "niet meedoen" krijgen heldere tinten.
                        </p>
                        <p className="mb-8">
                            Het werk is ingelijst in een houten frame met glas. Er zijn lichte beschadigingen aan de lijsthoeken zichtbaar (zie de productfoto’s), maar het zeefdruk zelf verkeert in perfecte staat.
                        </p>

                        <div className="my-20 p-12 bg-gray-50 border-l-4 border-accent shadow-sm">
                            <p className="text-xl font-serif italic text-primary leading-relaxed">
                                "Bij Briels is de menselijke figuur altijd een standpunt. Niet wie iemand is, maar hoe hij of zij zich verhoudt tot de wereld om hem heen."
                            </p>
                        </div>

                        {/* === BLOK 3: Emotie in het originele schilderij === */}
                        <h2 className="text-3xl font-serif text-primary mt-20 mb-8 underline decoration-accent decoration-1 underline-offset-8">Wat je ziet voor een origineel: het schilderij</h2>
                        <p className="mb-8">
                            Er is een wezenlijk verschil tussen een afbeelding van een Briels zien en voor het origineel staan. Bij zijn zeefdrukken is dat verschil al aanzienlijk; bij zijn originele schilderijen is het onmisbaar.
                        </p>
                        <p className="mb-8">
                            Het schilderij <strong><em>You carved out your name in my heart, I was touched to the core</em></strong> (acryl op doek, 130 bij 100 cm, 2004, 1/1 exemplaar) heeft een oppervlaktewerking die reproducties niet kunnen weergeven. De acrylverf is in meerdere lagen opgebouwd; de diepere lagen zijn zichtbaar door de bovenste lagen heen en geven het werk een dimensie die je pas begrijpt als je het van dichtbij bekijkt. De gestileerde figuren, het thema van liefde en blijvende indruk, de signatuur van Briels rechtsonder op het doek: het zijn elementen die samen pas volledig tot hun recht komen in het origineel.
                        </p>
                        <p className="mb-8">
                            Dit is het enige exemplaar. Een origineel schilderij van Briels is per definitie een 1/1 object. Er is geen tweede versie, geen editie om naar te vergelijken.
                        </p>

                        {/* === BLOK 4: 3D werk en ruimtelijke aanwezigheid === */}
                        <h2 className="text-3xl font-serif text-primary mt-20 mb-8 underline decoration-accent decoration-1 underline-offset-8">Ruimtelijke aanwezigheid: het 3D wandobject</h2>
                        <p className="mb-8">
                            Het wandobject <strong><em>Yellow Beard of Vegas</em></strong> (acryl op hars, 49 bij 49 bij 7 cm, 2000, editie 4/50) is een categorie apart. Het is geen schilderij dat aan de muur hangt; het is een sculptuur die toevallig aan de muur bevestigd is.
                        </p>
                        <p className="mb-8">
                            De 7 centimeter diepte verandert de relatie met het werk fundamenteel. Het neemt ruimte in, werpt schaduwen, reageert op het licht in de kamer. Het harsoppervlak heeft een reflectie die acryl op doek niet heeft: matte zones naast glanzende zones, afhankelijk van de hoek waarop je kijkt. Op verschillende momenten van de dag toont het werk andere kleuraccenten.
                        </p>
                        <p className="mb-8">
                            Editie 4/50 betekent: één van vijftig exemplaren wereldwijd. Handgeschilderd acryl op hars is een arbeidsintensief procédé waarbij elke laag tijd nodig heeft om te drogen voor de volgende aangebracht kan worden. Dat maakt elk exemplaar subtiel uniek.
                        </p>

                        {/* === BLOK 5: Marktcontext === */}
                        <h2 className="text-3xl font-serif text-primary mt-20 mb-8 underline decoration-accent decoration-1 underline-offset-8">Beschikbaarheid en marktpositie</h2>
                        <p className="mb-8">
                            De werken van Briels bewegen zich steeds vaker op de secundaire markt. De zeefdrukken in deze collectie (edities van 175 stuks) zijn al jaren niet meer te koop bij reguliere galeries of kunstcentra. Wie nu een Briels-zeefdruk wil kopen, is aangewezen op particuliere verkopen of veilinghuizen.
                        </p>
                        <p className="mb-8">
                            Veilingresultaten voor Briels zijn te volgen via Invaluable en Artnet. Zijn werk hangt in de collecties van KLM, Philips, ABN AMRO en Ford. Hij was officieel Olympisch Kunstenaar bij de Winterspelen van Salt Lake City in 2002, als erkenning van internationale reikwijdte. Dat zijn verifieerbare feiten, geen marketing.
                        </p>
                        <p className="mb-8">
                            Alle vier de werken in deze collectie worden geleverd met een certificaat van echtheid, persoonlijk gesigneerd door Clemens Briels. De provenance van elk werk is gedocumenteerd.
                        </p>

                    </div>

                    {/* FAQ */}
                    <div className="mt-24 pt-20 border-t border-gray-100">
                        <h2 className="text-3xl font-serif text-primary mb-12">Veelgestelde vragen over kleur en symboliek</h2>
                        <div className="space-y-8">
                            {[
                                {
                                    q: 'Wat betekent de kleur in het werk van Clemens Briels?',
                                    a: 'Kleur heeft bij Clemens Briels geen decoratieve functie maar is het primaire communicatiemiddel. Kleurkeuzes volgen een emotionele logica in plaats van realisme: een compositie kan tegelijk warm en koud aanvoelen, afhankelijk van welk vlak de aandacht trekt. Dat is goed zichtbaar in de zeefdruk Antigua, a bay a day.'
                                },
                                {
                                    q: 'Waarom zijn de figuren in het werk van Clemens Briels geen portretten?',
                                    a: 'De menselijke figuren bij Clemens Briels zijn nooit portrettistisch bedoeld. Het zijn typen en archetypes die een houding of emotie vertegenwoordigen, geen specifieke persoon. In Algunos Gentes Hacen la Comedia, Otros No plaatst hij bijvoorbeeld figuren naast elkaar die elk hun eigen verhouding tot het thema van de titel uitdrukken.'
                                },
                                {
                                    q: 'Wat maakt een 3D wandobject van Clemens Briels anders dan een schilderij?',
                                    a: 'Een 3D wandobject zoals Yellow Beard of Vegas is acryl op hars met enkele centimeters diepte, waardoor het functioneert als sculptuur in plaats van een vlak beeld. Het harsoppervlak reflecteert licht anders dan doek: matte en glanzende zones wisselen elkaar af afhankelijk van de kijkhoek.'
                                },
                            ].map(({ q, a }) => (
                                <div key={q} className="border-b border-gray-100 pb-8">
                                    <h3 className="font-serif text-primary text-xl mb-3">{q}</h3>
                                    <p className="text-gray-500 font-light leading-relaxed">{a}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="mt-20 flex flex-col sm:flex-row gap-6 justify-center items-center">
                        <Link
                            to="/#collectie"
                            className="inline-block px-12 py-5 border border-primary text-primary hover:bg-primary hover:text-white transition-all duration-500 uppercase text-[10px] tracking-[0.3em] font-bold"
                        >
                            Bekijk de Collectie
                        </Link>
                        <Link
                            to="/antipodisme"
                            className="inline-block px-12 py-5 bg-primary text-white hover:bg-accent transition-all duration-500 uppercase text-[10px] tracking-[0.3em] font-bold shadow-xl"
                        >
                            Over het Antipodisme
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default SymboliekPage;
